import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, ArrowUpRightIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useLanguage } from '../components/LanguageContext';
import { trackEvent as track } from '../utils/analytics';
const reveal = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.7
  }
};
export function TelecommunicationsMedia() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  useEffect(() => {
    track('Page Viewed', {
      page: 'Telecommunications & Media'
    });
  }, []);
  return <div className="w-full min-h-screen bg-white" data-id="element-8732" data-mp="3sqkn3f9pduao">
      <Header data-id="element-8733" data-mp="1lkvnzktkvid" />
      <main className="pt-[120px]" data-id="element-8734" data-mp="1m448poky7jq">
        {/* SECTION 1: HERO */}
        <section className="pt-24 pb-28 px-6" data-id="element-8735" data-mp="6jw5wbf11814">
          <div className="max-w-3xl mx-auto" data-id="element-8736" data-mp="6knqkr735lcm">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8
          }} data-id="element-8737" data-mp="2v2f9wqnu0ub5">
              <div className="font-sans text-xs font-semibold tracking-[0.2em] text-[#4F46E5] uppercase mb-8" data-id="element-8738" data-mp="2q3cd1ti09ue9">
                {isFR ? 'SECTEURS · TÉLÉCOMMUNICATIONS ET MÉDIAS' : 'SECTORS · TELECOMMUNICATIONS & MEDIA'}
              </div>
              <h1 className="font-serif text-5xl md:text-6xl text-[#121212] leading-tight mb-8" data-id="element-8739" data-mp="2h4qlx9ygx71v">
                {isFR ? 'Des abonnés anonymes. Ou une communauté engagée.' : 'Anonymous subscribers. Or an engaged community.'}
              </h1>
              <p className="font-serif text-2xl md:text-3xl text-[#121212] leading-snug mb-10" data-id="element-8740" data-mp="3434ypaeodkfd">
                {isFR ? 'Les télécoms multiplient les projets pilotes d’IA partout — chaque équipe dans son silo, des projets en double que personne ne partage. Les deux tiers n’atteignent jamais la production.' : 'Telecoms are running AI pilots everywhere — each team in its own silo, duplicate projects no one shares. Two-thirds never reach production.'}
              </p>
              <div className="flex flex-wrap items-center gap-4" data-id="element-8741" data-mp="1uv1q5ylt7ud2">
                <Link to="/request-demo?industry=telecom" onClick={() => track('Demo CTA Clicked', {
                location: 'Telecom Hero',
                cta: 'Talk to us'
              })} className="inline-flex items-center gap-2 px-8 py-4 bg-[#4F46E5] text-white text-base font-medium rounded-none hover:bg-[#4F46E5]/90 transition-colors" data-id="element-8742" data-mp="1uwctjxbcf03n">
                  {isFR ? 'Parlez-nous' : 'Talk to us'}
                  <ArrowRightIcon className="w-4 h-4" data-id="element-8743" data-mp="1c4sdtvwb6m7n" />
                </Link>
                <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
                location: 'Telecom Hero',
                cta: 'Request a Demo'
              })} className="inline-flex items-center gap-2 px-8 py-4 border border-[#E4E6EC] text-[#121212] text-base font-medium rounded-none hover:border-[#4F46E5] hover:text-[#4F46E5] transition-colors" data-id="element-8744" data-mp="x7jlozz6leev">
                  {isFR ? 'Demander une démo' : 'Request a demo'}
                  <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-8745" data-mp="kvz7pqkzp5qx" />
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* SECTION 2: RESEARCH SHOWS */}
        <section className="py-24 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-8746" data-mp="undfsmr0bdvo">
          <div className="max-w-3xl mx-auto" data-id="element-8747" data-mp="pv3hojfqn7ib">
            <motion.div {...reveal} className="font-sans text-xs font-semibold tracking-widest text-[#121212]/40 uppercase mb-8" data-id="element-8748" data-mp="puaji6uuhqbs">
              {isFR ? 'La recherche montre' : 'Research shows'}
            </motion.div>
            <motion.p {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="font-sans text-base text-[#121212]/55 leading-relaxed flex items-start gap-3 mb-8" data-id="element-8749" data-mp="znrdbauzf5fg">
              <span className="mt-2 inline-block w-1.5 h-1.5 bg-[#121212]/30 shrink-0" aria-hidden="true" data-id="element-8750" data-mp="18mmkap5uojjt" />
              <span data-id="element-8751" data-mp="3fct2b5j70gfi">
                {isFR ? 'Les entreprises dont les données sont bien intégrées obtiennent un RCI de 10,3× sur l’IA, contre 3,7× pour celles à faible connectivité. ' : 'Companies with well-integrated data get 10.3× ROI from AI, versus 3.7× for those with poor connectivity. '}
                <a href="https://www.integrate.io/blog/data-transformation-challenge-statistics/" target="_blank" rel="noopener noreferrer" className="text-[#4F46E5] underline underline-offset-2 hover:text-[#4F46E5]/80 transition-colors" data-id="element-8752" data-mp="3lt2ol5mba0lv">
                  {isFR ? 'Voir la recherche' : 'See the research'}
                </a>
                .
              </span>
            </motion.p>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="border-t border-[#E4E6EC] pt-6 text-xs text-[#121212]/40 leading-relaxed" data-id="element-8753" data-mp="2y0w0suunj1y2">
              {isFR ? 'Contexte de marché : il ne s’agit pas de résultats de déploiement BCITI. Source : MuleSoft 2025 Connectivity Benchmark, via Integrate.io.' : 'Market context. Not BCITI deployment results. Source: MuleSoft 2025 Connectivity Benchmark, via Integrate.io.'}
            </motion.div>
          </div>
        </section>

        {/* SECTION 3: BCITI ANSWER */}
        <section className="py-28 px-6 border-t border-[#E4E6EC]" data-id="element-8754" data-mp="sgssl3hj2ag2">
          <div className="max-w-3xl mx-auto" data-id="element-8755" data-mp="nfvccwg2vqh7">
            <motion.p {...reveal} className="font-serif text-3xl md:text-4xl text-[#121212] leading-snug" data-id="element-8756" data-mp="nj0z4518ig9r">
              {isFR ? 'L’IA télécom meurt dans les silos. Une fondation d’identité vérifiée — un abonné, une vue — transforme des pilotes éparpillés en intelligence durable, et des abonnés anonymes en relations durables.' : 'Telecom AI dies in silos. A verified-identity foundation — one subscriber, one view — turns scattered pilots into lasting intelligence, and anonymous subscribers into lasting relationships.'}
            </motion.p>
          </div>
        </section>
      </main>
      <Footer data-id="element-8757" data-mp="3lytwoz1lzdn5" />
    </div>;
}